import { effectiveLocationId, type DailyRevenue, type ExpenseCategory, type LineaResultado, type Location, type ProfitAndLoss, type Target } from './types';
import { addMonths, monthOf, periodOfMonth } from './period';
import { round } from './units';
import { diasDelMes, diasSinAnotar, estadoDia, porFecha } from './ventas';
import { mesEnPalabras, nombreMesCorto } from './repartos';
import { textoAppSinComisiones, type AppSinComisiones } from './comisionesApps';

/**
 * ESTADO DE RESULTADOS (decidido con Alex el 11 oct 2026).
 *
 * Funciones puras para la pantalla: el mes por defecto, los avisos de calidad,
 * la frase que lo resume, la barra «De cada $100», las filas (vista
 * comprimida con sus grupos) y la tabla para descargar. Los números salen
 * TODOS de `computeProfitAndLoss`: aquí solo se ordenan y se nombran.
 */

/** Cómo se escriben el dinero y los % en la pantalla (según el país). */
export interface FormatoCifras {
  money: (n: number) => string;
  pct: (n: number) => string;
}

export type Tono = 'verde' | 'ambar' | 'rojo';

const capital = (t: string) => (t ? t[0].toUpperCase() + t.slice(1) : t);
const ultimoDia = (month: string) => Number(periodOfMonth(month).end.slice(8, 10));
/** «30 sep». */
const finDeMesCorto = (month: string) => `${ultimoDia(month)} ${nombreMesCorto(month)}`;
/** «2026-10-17» → «17 oct». */
export const fechaCorta = (date: string) => `${Number(date.slice(8, 10))} ${nombreMesCorto(date.slice(0, 7))}`;

/* ------------------------------------------------------------------ */
/* Periodo                                                             */
/* ------------------------------------------------------------------ */

/** Por defecto, el último mes cerrado (el anterior al de hoy). */
export function ultimoMesCerrado(hoy: string): string {
  return addMonths(monthOf(hoy), -1);
}

export type RangoMeses = '3' | '6' | '12' | 'anio';

/** «Últimos 3 meses cerrados / 6 / 12 / Este año»: siempre meses cerrados, del más viejo al más nuevo. */
export function mesesCerrados(rango: RangoMeses, hoy: string): string[] {
  const ultimo = ultimoMesCerrado(hoy);
  if (rango === 'anio') {
    const enero = `${hoy.slice(0, 4)}-01`;
    if (ultimo < enero) return [ultimo];
    const out: string[] = [];
    for (let m = enero; m <= ultimo; m = addMonths(m, 1)) out.push(m);
    return out;
  }
  const n = Number(rango);
  return Array.from({ length: n }, (_, i) => addMonths(ultimo, i - n + 1));
}

/* ------------------------------------------------------------------ */
/* Objetivos y colores                                                 */
/* ------------------------------------------------------------------ */

export type ObjetivosResultado = Partial<Pick<Target, 'foodCostPct' | 'laborCostPct' | 'primeCostPct' | 'ebitdaPct'>>;

/**
 * Color solo en el % de los grupos con objetivo (regla 3): verde si está
 * dentro, ámbar si se pasa hasta 3 puntos, rojo si se pasa más. En el EBITDA
 * «pasarse» es quedarse por debajo.
 */
export function tonoObjetivo(pct: number, objetivo: number | undefined, masEsMejor = false): Tono | undefined {
  if (objetivo == null || !Number.isFinite(pct)) return undefined;
  const exceso = round(masEsMejor ? objetivo - pct : pct - objetivo, 1);
  if (exceso <= 0) return 'verde';
  return exceso <= 3 ? 'ambar' : 'rojo';
}

const pctDe = (importe: number, ventas: number) => (ventas > 0 ? round((importe / ventas) * 100, 1) : 0);

/**
 * El % que se ENSEÑA (revisión de Alex del 14 oct 2026): entero, salvo que el
 * redondeo lo iguale al objetivo sin serlo — «30,3 %» con objetivo 30 %, no
 * «30 %» en ámbar. El mismo en la frase y en la tabla; el color, siempre con
 * el valor real (`tonoObjetivo`).
 */
export function pctParaMostrar(pct: number, objetivo?: number): number {
  if (!Number.isFinite(pct)) return 0;
  const entero = Math.round(pct);
  const decimal = round(pct, 1);
  if (objetivo != null && entero === round(objetivo, 1) && decimal !== round(objetivo, 1)) return decimal;
  return entero;
}

/* ------------------------------------------------------------------ */
/* Avisos de calidad (regla 4)                                         */
/* ------------------------------------------------------------------ */

export interface AvisoCalidad {
  tono: 'verde' | 'ambar' | 'gris';
  clave: 'completo' | 'sin-recuento' | 'recuento-estimado' | 'faltan-dias' | 'en-curso' | 'cambio' | 'sin-comisiones';
  texto: string;
  accion?: { texto: string; ruta: string };
}

export interface CalidadInput {
  month: string;
  hoy: string;
  pnl: Pick<ProfitAndLoss, 'existencias' | 'hasInventory' | 'cambiosPorRepartos'>;
  /** Ventas del mes (de todos los locales que se ven). */
  revenues: Array<Pick<DailyRevenue, 'date' | 'inHouse' | 'delivery' | 'closed' | 'takeaway'> & { locationId?: string }>;
  /** Días que cierra (0 = domingo … 6 = sábado): los de la cuenta. */
  closedWeekdays?: number[];
  /**
   * Los locales que se miran: cada uno con SUS días de cierre (Configuración
   * los guarda en el local) y sus propias ventas. Un día falta si le falta a
   * alguno. Ausente o vacío = como antes (todas las ventas juntas).
   */
  locales?: Array<Pick<Location, 'locationId' | 'closedWeekdays'>>;
  /** Apps con ventas ese mes y sin factura de comisiones (`appsSinFacturaDeComisiones`). */
  appsSinComisiones?: AppSinComisiones[];
  fmt: FormatoCifras;
}

/** ¿Tiene el mes su recuento de fin de mes (cerrado)? */
export function tieneRecuentoDeCierre(pnl: Pick<ProfitAndLoss, 'existencias'>, month: string): boolean {
  const final = pnl.existencias?.final;
  return Boolean(final?.mes === month && !final.deOtroMes && !final.faltanLocales?.length);
}

/** Días del mes ya pasados sin ventas anotadas ni marcados como cerrados. */
export function diasDeVentasQueFaltan(month: string, revenues: CalidadInput['revenues'], closedWeekdays: number[] | undefined, hoy: string): string[] {
  return diasSinAnotar(month, revenues.filter((r) => r.date.startsWith(month)) as Parameters<typeof diasSinAnotar>[1], closedWeekdays, hoy);
}

/**
 * Días que faltan en los locales que se miran: local por local, con los días
 * que cierra cada uno (los del local o, si no tiene, los de la cuenta). Un día
 * falta si le falta a alguno. Sin locales, como `diasDeVentasQueFaltan`.
 */
export function diasQueFaltanDelAmbito(
  month: string,
  revenues: CalidadInput['revenues'],
  closedWeekdays: number[] | undefined,
  hoy: string,
  locales?: CalidadInput['locales'],
): string[] {
  if (!locales?.length) return diasDeVentasQueFaltan(month, revenues, closedWeekdays, hoy);
  const delMes = revenues.filter((r) => r.date.startsWith(month)) as unknown as Array<NonNullable<Parameters<typeof estadoDia>[1]> & { date: string; locationId?: string }>;
  const faltan = new Set<string>();
  for (const l of locales) {
    const mapa = porFecha(delMes.filter((r) => effectiveLocationId(r.locationId) === l.locationId));
    const cerrados = l.closedWeekdays ?? closedWeekdays ?? [];
    for (const d of diasDelMes(month)) if (estadoDia(d, mapa.get(d), cerrados, hoy) === 'sin_anotar') faltan.add(d);
  }
  return [...faltan].sort();
}

/**
 * Datos completos o nada (regla 1 de Análisis): un mes es de fiar si ya
 * terminó, tiene las ventas de todos los días abiertos y su recuento.
 */
export function mesCompleto(input: Omit<CalidadInput, 'fmt'>): boolean {
  const { month, hoy } = input;
  if (month >= monthOf(hoy)) return false;
  return tieneRecuentoDeCierre(input.pnl, month) && diasQueFaltanDelAmbito(month, input.revenues, input.closedWeekdays, hoy, input.locales).length === 0;
}

/**
 * Los avisos de arriba, uno por línea y solo si aplican (regla 4):
 *  - Falta el recuento → ámbar, y se dice qué recuento se usó.
 *  - Faltan días de ventas → ámbar.
 *  - Mes en curso → gris.
 *  - Todo completo → verde.
 *  - Un pago repartido cambió el mes cuando ya estaba cerrado → gris (regla 5
 *    de pagos repartidos; el verde se mantiene).
 */
export function avisosDeCalidad(input: CalidadInput): AvisoCalidad[] {
  const { month, hoy, pnl, fmt } = input;
  const Mes = capital(mesEnPalabras(month));
  const avisos: AvisoCalidad[] = [];
  const faltan = diasQueFaltanDelAmbito(month, input.revenues, input.closedWeekdays, hoy, input.locales);
  const avisoFaltan = (): AvisoCalidad => ({
    tono: 'ambar',
    clave: 'faltan-dias',
    texto: faltan.length === 1
      ? 'Falta 1 día de ventas: los % salen más altos de lo real.'
      : `Faltan ${faltan.length} días de ventas: los % salen más altos de lo real.`,
    accion: { texto: faltan.length === 1 ? 'Anotarlo' : 'Anotarlos', ruta: `/ingresos?mes=${month}` },
  });

  if (month >= monthOf(hoy)) {
    const dia = Number(hoy.slice(8, 10));
    avisos.push({
      tono: 'gris',
      clave: 'en-curso',
      texto: month > monthOf(hoy)
        ? `${Mes} todavía no empieza.`
        : `${Mes} va por el día ${dia}: la renta y las nóminas ya están, las ventas no. No lo compares todavía.`,
    });
    if (faltan.length > 0) avisos.push(avisoFaltan());
    return avisos;
  }

  if (!tieneRecuentoDeCierre(pnl, month)) {
    const final = pnl.existencias?.final;
    const usado = final?.mes && final.deOtroMes
      ? ` Uso el último que hiciste, el de ${mesEnPalabras(final.mes)} (${fmt.money(final.valor)}).`
      : '';
    avisos.push({
      tono: 'ambar',
      clave: 'sin-recuento',
      texto: `Sin recuento de fin de ${mesEnPalabras(month)}, la materia prima sale más alta de lo real.${usado}`,
      // Al mes que falta (14 oct 2026): si su ventana pasó, ahí sale el «Cierre atrasado», nunca un recuento imposible.
      accion: { texto: 'Hacer el recuento', ruta: `/inventario?mes=${month}` },
    });
  } else if (pnl.existencias?.final?.estimado) {
    avisos.push({
      tono: 'gris',
      clave: 'recuento-estimado',
      texto: `El recuento de fin de ${mesEnPalabras(month)} es una estimación: se contó tarde y Alexia restó lo que compraste después.`,
      accion: { texto: 'Ver el recuento', ruta: `/inventario?mes=${month}` },
    });
  }
  if (faltan.length > 0) avisos.push(avisoFaltan());
  // Sin la factura de comisiones de una app, el resultado sale más alto de lo real (14 oct 2026).
  for (const app of input.appsSinComisiones ?? []) {
    avisos.push({
      tono: 'ambar',
      clave: 'sin-comisiones',
      texto: textoAppSinComisiones(app, fmt.money, `En ${mesEnPalabras(month)}`),
      accion: { texto: 'Subir la factura', ruta: `/gastos?mes=${month}` },
    });
  }
  if (avisos.length === 0) {
    avisos.push({
      tono: 'verde',
      clave: 'completo',
      texto: `${Mes} está completo: ventas de todos los días y recuento del ${finDeMesCorto(month)}. Estos números son de fiar.`,
    });
  }
  for (const cambio of pnl.cambiosPorRepartos ?? []) {
    avisos.push({
      tono: 'gris',
      clave: 'cambio',
      texto: `${Mes} cambió el ${fechaCorta(cambio.fecha)}: se le sumaron ${fmt.money(cambio.importe)} de «${cambio.concepto}», un pago repartido entre varios meses.`,
    });
  }
  return avisos;
}

/* ------------------------------------------------------------------ */
/* Frase y barra «De cada $100»                                        */
/* ------------------------------------------------------------------ */

interface Exceso { nombre: string; pct: number; objetivo: number; exceso: number }

function excesos(pnl: ProfitAndLoss, objetivos: ObjetivosResultado | undefined): Exceso[] {
  if (!objetivos || !(pnl.revenue > 0)) return [];
  const v = pnl.revenue;
  const candidatos: Array<[string, number, number | undefined, boolean]> = [
    ['la materia prima', pctDe(pnl.materialConsumption, v), objetivos.foodCostPct, false],
    ['el personal', pctDe(pnl.laborCost, v), objetivos.laborCostPct, false],
    ['la materia prima + personal', pctDe(pnl.primeCost, v), objetivos.primeCostPct, false],
    ['lo que te dejó el negocio', pctDe(pnl.ebitda, v), objetivos.ebitdaPct, true],
  ];
  return candidatos
    .filter(([, , objetivo]) => objetivo != null)
    .map(([nombre, pct, objetivo, mas]) => ({ nombre, pct, objetivo: objetivo!, exceso: round(mas ? objetivo! - pct : pct - objetivo!, 1) }))
    .filter((e) => e.exceso > 0)
    .sort((a, b) => b.exceso - a.exceso);
}

/**
 * «En septiembre vendiste $X y el negocio te dejó $Y, el 21 %. Lo que más se
 * pasa de tu objetivo: la materia prima, 32 % (tu objetivo: 30 %).»
 */
export function fraseResumen(month: string, pnl: ProfitAndLoss, objetivos: ObjetivosResultado | undefined, fmt: FormatoCifras): string {
  const mes = mesEnPalabras(month);
  if (!(pnl.revenue > 0)) return `En ${mes} no hay ventas anotadas: sin ventas no se puede saber cuánto te dejó el negocio.`;
  const primera = pnl.ebitda >= 0
    ? `En ${mes} vendiste ${fmt.money(pnl.revenue)} y el negocio te dejó ${fmt.money(pnl.ebitda)}, el ${fmt.pct(pctParaMostrar(pctDe(pnl.ebitda, pnl.revenue), objetivos?.ebitdaPct))}.`
    : `En ${mes} vendiste ${fmt.money(pnl.revenue)} y los gastos se pasaron de lo vendido en ${fmt.money(-pnl.ebitda)}.`;
  const tieneObjetivos = objetivos && Object.values(objetivos).some((v) => v != null);
  if (!tieneObjetivos) return primera;
  const [peor] = excesos(pnl, objetivos);
  if (!peor) return `${primera} Todo está dentro de tus objetivos.`;
  return `${primera} Lo que más se pasa de tu objetivo: ${peor.nombre}, ${fmt.pct(pctParaMostrar(peor.pct, peor.objetivo))} (tu objetivo: ${fmt.pct(peor.objetivo)}).`;
}

export interface TramoDeCada100 {
  clave: 'materia_prima' | 'personal' | 'generales' | 'te_dejo';
  nombre: string;
  /** De cada 100 vendidos (redondeado; los cuatro suman 100). */
  valor: number;
}

/**
 * «De cada $100 que vendiste»: $32 materia prima · $29 personal · $18 gastos
 * generales · $21 te dejó el negocio. Si los gastos pasan de lo vendido,
 * «te dejó» es 0 y `faltan` dice cuánto faltó de cada 100.
 */
/** Redondea a enteros que suman exactamente `total` (método del mayor resto). */
export function mayorResto(valores: number[], total = 100): number[] {
  const base = valores.map((v) => Math.floor(v));
  let falta = total - base.reduce((a, b) => a + b, 0);
  const orden = valores
    .map((v, i) => ({ i, resto: v - Math.floor(v) }))
    .sort((a, b) => b.resto - a.resto || a.i - b.i);
  for (let k = 0; falta > 0 && orden.length; k = (k + 1) % orden.length, falta--) base[orden[k].i]++;
  return base;
}

export function deCada100(pnl: ProfitAndLoss): { tramos: TramoDeCada100[]; faltan: number } | null {
  const v = pnl.revenue;
  if (!(v > 0)) return null;
  const mp = Math.max(0, (pnl.materialConsumption / v) * 100);
  const pe = Math.max(0, (pnl.laborCost / v) * 100);
  const ge = Math.max(0, (pnl.operatingExpenses / v) * 100);
  const gastos = mp + pe + ge;
  if (gastos <= 100) {
    const [a, b, c, queda] = mayorResto([mp, pe, ge, Math.max(0, 100 - gastos)]);
    const r = [a, b, c];
    return {
      tramos: [
        { clave: 'materia_prima', nombre: 'Materia prima', valor: r[0] },
        { clave: 'personal', nombre: 'Personal', valor: r[1] },
        { clave: 'generales', nombre: 'Gastos generales', valor: r[2] },
        { clave: 'te_dejo', nombre: 'Te dejó el negocio', valor: queda },
      ],
      faltan: 0,
    };
  }
  // Se pasa de 100: los tramos se escalan a 100 y se dice cuánto faltó.
  const k = 100 / gastos;
  const r = mayorResto([mp * k, pe * k, ge * k]);
  return {
    tramos: [
      { clave: 'materia_prima', nombre: 'Materia prima', valor: r[0] },
      { clave: 'personal', nombre: 'Personal', valor: r[1] },
      { clave: 'generales', nombre: 'Gastos generales', valor: r[2] },
      { clave: 'te_dejo', nombre: 'Te dejó el negocio', valor: 0 },
    ],
    faltan: Math.round(gastos - 100),
  };
}

/* ------------------------------------------------------------------ */
/* Filas (vista comprimida, grupos plegables)                          */
/* ------------------------------------------------------------------ */

export type TipoFila = 'ventas' | 'grupo' | 'linea' | 'variacion' | 'indicador' | 'total' | 'abajo' | 'neta' | 'tecnica';

export interface FilaEstado {
  /** Estable entre meses: así «Mes a mes» alinea las columnas. */
  id: string;
  tipo: TipoFila;
  /** Nombre de dueño: «Te dejó el negocio». */
  nombre: string;
  /** Término técnico, en gris al lado: «EBITDA». */
  tecnico?: string;
  /** En positivo; solo la variación de inventario lleva signo. */
  importe: number;
  /** % sobre lo vendido. */
  pct: number;
  objetivo?: number;
  tono?: Tono;
  /** Explicación en gris debajo («Había $9,687.51 el 1 sep…»). */
  nota?: string;
  /** Categoría propia del usuario («tuya»). */
  propia?: boolean;
  hijos?: FilaEstado[];
}

export interface OpcionesFilas {
  nombreCategoria: (c: ExpenseCategory) => string;
  fmt: FormatoCifras;
  /** Para la descarga del contador: EBIT y resultado antes de impuestos. */
  conFilasTecnicas?: boolean;
}

function lineasDe(pnl: ProfitAndLoss, grupo: LineaResultado['grupo'], ventas: number, opts: OpcionesFilas): FilaEstado[] {
  return (pnl.lineas ?? [])
    .filter((l) => l.grupo === grupo)
    .map((l) => {
      const id = l.compartidos ? `${grupo}|compartidos` : l.propia ? `${grupo}|propia|${l.propia}` : `${grupo}|${l.categoria}`;
      const nombre = l.compartidos ? 'Gastos compartidos (tu parte)' : l.propia ?? opts.nombreCategoria(l.categoria!);
      const nota = l.repartos?.length
        ? l.repartos.map((r) => `${r.concepto} (${r.n} de ${r.de}): ${opts.fmt.money(r.importe)}`).join(' · ')
        : undefined;
      return { id, tipo: 'linea' as const, nombre, importe: l.importe, pct: pctDe(l.importe, ventas), ...(nota ? { nota } : {}), ...(l.propia ? { propia: true } : {}) };
    });
}

/** La línea «Quedó más / menos en el almacén que al empezar», con su explicación. */
function filaVariacion(month: string | undefined, pnl: ProfitAndLoss, ventas: number, fmt: FormatoCifras): FilaEstado | undefined {
  if (!pnl.hasInventory) return undefined;
  const inicial = pnl.existencias?.inicial ?? { valor: pnl.openingInventory };
  const final = pnl.existencias?.final ?? { valor: pnl.closingInventory };
  const importe = round(inicial.valor - final.valor, 2);
  const mes = month ?? pnl.periodStart.slice(0, 7);
  const mesFin = pnl.periodEnd.slice(0, 7);
  const habia = inicial.mes
    ? `Había ${fmt.money(inicial.valor)} el 1 ${nombreMesCorto(mes)}`
    : `No había recuento antes del 1 ${nombreMesCorto(mes)}`;
  const queda = final.mes && !final.deOtroMes
    ? `${fmt.money(final.valor)} en el recuento del ${finDeMesCorto(mesFin)}`
    : final.mes
      ? `no hay recuento de fin de ${mesEnPalabras(mesFin)}: uso el último, ${fmt.money(final.valor)} (${mesEnPalabras(final.mes)})`
      : 'no hay recuento de fin de mes';
  return {
    id: 'materia_prima|variacion',
    tipo: 'variacion',
    nombre: importe < 0 ? 'Quedó más en el almacén que al empezar' : importe > 0 ? 'Quedó menos en el almacén que al empezar' : 'Quedó lo mismo en el almacén que al empezar',
    importe,
    pct: pctDe(importe, ventas),
    nota: `${habia} y ${queda}.`,
  };
}

/**
 * Las filas del Estado de resultados (regla 1): Vendiste · Materia prima ·
 * Personal · (materia prima + personal, en gris) · Gastos generales · TE DEJÓ
 * EL NEGOCIO · (plegado: desgaste, intereses, impuestos) · Ganancia neta.
 * Cada grupo trae sus líneas en `hijos`. Todo en positivo.
 */
export function filasDelEstado(pnl: ProfitAndLoss, objetivos: ObjetivosResultado | undefined, opts: OpcionesFilas, month?: string): FilaEstado[] {
  const v = pnl.revenue;
  const o = objetivos ?? {};
  const conObjetivo = (fila: FilaEstado, objetivo: number | undefined, mas = false): FilaEstado =>
    objetivo == null ? fila : { ...fila, objetivo, ...(v > 0 ? { tono: tonoObjetivo(fila.pct, objetivo, mas) } : {}) };

  const variacion = filaVariacion(month, pnl, v, opts.fmt);
  const abajoHijos: FilaEstado[] = [
    { id: 'abajo|desgaste', tipo: 'linea', nombre: 'Desgaste de equipos y obras', nota: 'Sale de Inversiones.', importe: pnl.depreciation, pct: pctDe(pnl.depreciation, v) },
    ...(opts.conFilasTecnicas ? [{ id: 'tecnica|ebit', tipo: 'tecnica' as const, nombre: 'Resultado de explotación', tecnico: 'EBIT', importe: pnl.ebit, pct: pctDe(pnl.ebit, v) }] : []),
    { id: 'abajo|intereses', tipo: 'linea', nombre: 'Intereses de préstamos', importe: pnl.financialExpenses, pct: pctDe(pnl.financialExpenses, v) },
    ...(opts.conFilasTecnicas ? [{ id: 'tecnica|ebt', tipo: 'tecnica' as const, nombre: 'Resultado antes de impuestos', importe: pnl.ebt, pct: pctDe(pnl.ebt, v) }] : []),
    { id: 'abajo|impuestos', tipo: 'linea', nombre: 'Impuestos sobre la ganancia', importe: pnl.taxes, pct: pctDe(pnl.taxes, v) },
  ];
  const abajo = round(pnl.depreciation + pnl.financialExpenses + pnl.taxes, 2);

  return [
    { id: 'ventas', tipo: 'ventas', nombre: 'Vendiste', importe: pnl.revenue, pct: v > 0 ? 100 : 0 },
    conObjetivo({
      id: 'materia_prima', tipo: 'grupo', nombre: 'Materia prima', importe: pnl.materialConsumption, pct: pctDe(pnl.materialConsumption, v),
      hijos: [...lineasDe(pnl, 'materia_prima', v, opts), ...(variacion ? [variacion] : [])],
    }, o.foodCostPct),
    conObjetivo({ id: 'personal', tipo: 'grupo', nombre: 'Personal', importe: pnl.laborCost, pct: pctDe(pnl.laborCost, v), hijos: lineasDe(pnl, 'personal', v, opts) }, o.laborCostPct),
    conObjetivo({ id: 'prime', tipo: 'indicador', nombre: 'Materia prima + personal', tecnico: 'prime cost', importe: pnl.primeCost, pct: pctDe(pnl.primeCost, v) }, o.primeCostPct),
    { id: 'generales', tipo: 'grupo', nombre: 'Gastos generales', importe: pnl.operatingExpenses, pct: pctDe(pnl.operatingExpenses, v), hijos: lineasDe(pnl, 'generales', v, opts) },
    conObjetivo({ id: 'ebitda', tipo: 'total', nombre: 'Te dejó el negocio', tecnico: 'EBITDA', importe: pnl.ebitda, pct: pctDe(pnl.ebitda, v) }, o.ebitdaPct, true),
    { id: 'abajo', tipo: 'abajo', nombre: 'Desgaste, intereses e impuestos', importe: abajo, pct: pctDe(abajo, v), hijos: abajoHijos },
    { id: 'neta', tipo: 'neta', nombre: 'Ganancia neta', importe: pnl.netProfit, pct: pctDe(pnl.netProfit, v) },
  ];
}

/* ------------------------------------------------------------------ */
/* Descarga para el contador (regla 7)                                 */
/* ------------------------------------------------------------------ */

export interface TablaDescarga {
  /** «Concepto», y por cada mes «Septiembre 2026» y «%». */
  cabecera: string[];
  filas: Array<{ nivel: 0 | 1 | 2; tipo: TipoFila; celdas: Array<string | number> }>;
}

/**
 * Todas las filas, abiertas, con EBIT y resultado antes de impuestos (para
 * el contador). Una columna de dinero y una de % por mes; con objetivos, una
 * columna «Tu objetivo» al final.
 */
export function tablaParaDescargar(
  meses: Array<{ month: string; pnl: ProfitAndLoss }>,
  objetivos: ObjetivosResultado | undefined,
  opts: Omit<OpcionesFilas, 'conFilasTecnicas'>,
): TablaDescarga {
  const porMes = meses.map(({ month, pnl }) => filasDelEstado(pnl, objetivos, { ...opts, conFilasTecnicas: true }, month));
  const nombreMes = (m: string) => `${capital(mesEnPalabras(m))} ${m.slice(0, 4)}`;
  const cabecera = ['Concepto', ...meses.flatMap(({ month }) => [nombreMes(month), '% vendido']), 'Tu objetivo'];
  // La forma: la unión de las filas de todos los meses, en orden de aparición.
  const orden: Array<{ fila: FilaEstado; nivel: 0 | 1 | 2 }> = [];
  const vistas = new Set<string>();
  const visitar = (filas: FilaEstado[], nivel: 0 | 1 | 2) => {
    for (const f of filas) {
      if (!vistas.has(f.id)) { vistas.add(f.id); orden.push({ fila: f, nivel }); }
      if (f.hijos) visitar(f.hijos, (nivel + 1) as 1 | 2);
    }
  };
  porMes.forEach((filas) => visitar(filas, 0));
  const buscar = (filas: FilaEstado[], id: string): FilaEstado | undefined => {
    for (const f of filas) {
      if (f.id === id) return f;
      const h = f.hijos && buscar(f.hijos, id);
      if (h) return h;
    }
    return undefined;
  };
  // Las líneas de cada grupo justo debajo de su grupo, aunque aparezcan en un mes posterior.
  const ordenados = orden.sort((a, b) => {
    const raiz = (id: string) => id.split('|')[0] === 'tecnica' ? 'abajo' : id.split('|')[0];
    const raices = ['ventas', 'materia_prima', 'personal', 'prime', 'generales', 'ebitda', 'abajo', 'neta'];
    const ra = raices.indexOf(raiz(a.fila.id));
    const rb = raices.indexOf(raiz(b.fila.id));
    if (ra !== rb) return ra - rb;
    if (a.nivel !== b.nivel) return a.nivel - b.nivel;
    return 0;
  });
  const filas = ordenados.map(({ fila, nivel }) => {
    const celdas: Array<string | number> = [`${'  '.repeat(nivel)}${fila.nombre}${fila.tecnico ? ` · ${fila.tecnico}` : ''}${fila.propia ? ' (tuya)' : ''}`];
    for (const delMes of porMes) {
      const f = buscar(delMes, fila.id);
      celdas.push(f ? f.importe : 0, f ? f.pct : 0);
    }
    celdas.push(fila.objetivo != null ? fila.objetivo : '');
    return { nivel, tipo: fila.tipo, celdas };
  });
  return { cabecera, filas };
}
